import { useMemo } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { RoomInfoProducts, RoomProduct } from "@b2bportal/lodging-api";
import {
  HotelRoomCardDialog,
  ImageCarousel,
  useHotelRoomCard,
  useHotelRoomCardTracking,
} from "@tenant/hotels";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { useHotelSlot } from "../../../../slots";
import {
  HotelRoomRateSourcesProvider,
  useHotelRoomRatesSource,
} from "../../hooks";
import { BREAKFAST_MEAL_PLAN_KINDS } from "../../hooks/lodgingFreeBreakfast";
import { getRoomLevelMealPlanKind } from "../../hooks/lodgingMealPlans";
import defaultStyles from "./HotelRoomCard.module.scss";

export interface HotelRoomCardComponentProps extends PlatformComponentProps {
  room: RoomInfoProducts;
  onRoomSelection: (product: RoomProduct) => void;
  nightCount: number;
  hotelHasPaidBreakfast?: boolean;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelRoomCard;

export const HotelRoomCard = (props: HotelRoomCardComponentProps) => {
  const fallbackHooks = useMemo(() => [useHotelRoomRatesSource], []);

  // Provider must wrap the inner component so useHotelRoomCard can access context
  // If a parent provider exists (e.g., from HotelRoomSelection or tenant), this fallback is skipped
  return (
    <HotelRoomRateSourcesProvider hooks={fallbackHooks}>
      <HotelRoomCardInner {...props} />
    </HotelRoomRateSourcesProvider>
  );
};

const HotelRoomCardInner = (props: HotelRoomCardComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const HotelSlot = useHotelSlot();

  const { className, children } = props;
  const { context, handlers, text } = useHotelRoomCard(props);
  const { ref } = useHotelRoomCardTracking(props);

  return (
    <div className={clsx(block, className)} ref={ref}>
      <div className={cn("image")}>
        <ImageCarousel imageUrlsArray={context.images} />
      </div>
      <div className={cn("room-name")}>{text.roomName}</div>
      <div className={cn("room-info")}>
        {text.guests != null ? (
          <div className={cn("guest")}>
            <Icon iconName={IconNameEnum.guest} />
            {text.guests}
          </div>
        ) : null}
        <div className={cn("bed")}>
          <Icon iconName={IconNameEnum.bed} />
          {text.beds}
        </div>
      </div>
      {context.mealPlanName != null ? (
        <div className={cn("meal-plan")}>
          <Icon
            iconName={(() => {
              const kind = getRoomLevelMealPlanKind(context.room);
              return kind != null && !BREAKFAST_MEAL_PLAN_KINDS.has(kind)
                ? IconNameEnum.hotel__allInclusive
                : IconNameEnum.hotel__breakfast;
            })()}
          />
          {context.mealPlanName}
        </div>
      ) : null}
      <HotelRoomCardDialog
        className={cn("view-details")}
        room={context.room}
        nightCount={context.nightCount}
        selectedProduct={context.selectedRate.product}
      />

      <div className={cn("divider")} />
      {/* Pass both Rate-based and legacy props - HotelRoomCardRates supports both APIs */}
      <HotelSlot.HotelRoomCardRates
        rates={context.rates}
        selectedRate={context.selectedRate}
        setSelectedRate={handlers.setSelectedRate}
        room={context.room}
        products={context.products}
        selectedProduct={context.selectedProduct}
        setSelectedProduct={handlers.setSelectedProduct}
        nightCount={context.nightCount}
        setSelectedRoom={handlers.setSelectedRoom}
      />
      <button
        className={cn("room-select-button", {
          cfar: context.selectedRate.origin === "cfar",
        })}
        onClick={() => handlers.setSelectedRoom(context.selectedRate.product)}
      >
        {text.roomCTA}
      </button>

      {children}
    </div>
  );
};
